"use client";

import type { LeadFlowDocument } from "../types";

import { cn } from "@homeinstead/ui";
import Stepper from "@homeinstead/ui/components/Stepper";
import { useCopy } from "../hooks/use-copy";
import { useLeadFlowRuntime } from "./LeadFlowRuntimeContext";

type LeadFlowProgressIndicator = NonNullable<
  NonNullable<LeadFlowDocument["presentation"]>["progressIndicator"]
>;

type ProgressProps = {
  indicator: LeadFlowProgressIndicator;
  onBack: (() => void) | null;
  position: number;
  total: number;
};

/**
 * The flow's progress bar. The `default` indicator adds the step count
 * and the in-form Back link above the bar; `bar` shows the bar alone. On a
 * Calm step the `default` indicator is the site's stepper, with Back and
 * the step count under the bar; in a section takeover it fills the question
 * column. It stays mounted across steps, so the bar grows from its current
 * width.
 */
function Progress({ indicator, onBack, position, total }: ProgressProps) {
  const { isSectionTakeover = false, stepTheme } = useLeadFlowRuntime();
  const label = useCopy().getStepProgressLabel(position, total);

  if (stepTheme === "calm" && indicator === "default") {
    return (
      // In the takeover the bar stops just short of the question column.
      <div
        className={cn(
          "w-full min-w-0",
          isSectionTakeover && "md:max-w-[27.5rem]",
        )}
        data-lead-flow-progress={indicator}
      >
        <Stepper
          ariaLabel={label}
          className="w-full"
          currentStep={position}
          handleBack={onBack ?? (() => {})}
          labelRowClassName="mb-0"
          showBackButton={onBack !== null}
          totalSteps={total}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-lead-flow-progress={indicator}>
      {indicator === "default" ? (
        <div className="flex items-center justify-between gap-4 text-sm">
          {onBack ? (
            <button
              className="font-semibold underline underline-offset-4"
              onClick={onBack}
              type="button"
            >
              Back
            </button>
          ) : (
            <span />
          )}
          <span aria-hidden="true">{label}</span>
        </div>
      ) : null}
      <div
        aria-label={label}
        aria-valuemax={total}
        aria-valuemin={0}
        aria-valuenow={position}
        className="h-2 w-full overflow-hidden rounded-full bg-gray"
        role="progressbar"
      >
        <div
          className="h-full rounded-full bg-success transition-[width] duration-300 ease-out motion-reduce:transition-none"
          style={{ width: `${total > 0 ? (position / total) * 100 : 0}%` }}
        />
      </div>
    </div>
  );
}

export { Progress };
